import { Component, computed, effect, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { MenuModule } from 'primeng/menu';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DynamicForm } from '../../../components/form-builder/form-builder.types';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DelaisReglementairesService } from './delais-reglementaires.service';
import { DelaisReglementaires } from '@/types';
import { getDelaisReglementairesDataTableStructure } from './utils/delais-reglementaires-data-table-object';
import { getDelaisReglementairesFormObject } from './utils/delais-reglementaires-form-object';
import { DataTableRowActions, DynamicDataTable } from '../../../components/data-table-builder/data-table.types';
import { DrawerComponent } from '../../../components/drawer/drawer.component';
import { DrawerObject } from '../../../components/drawer/drawer.types';
import { DataTableBuilderComponent } from '../../../components/data-table-builder/data-table-builder.component';
import { getDelaisReglementairesUpdateDrawerObject } from './utils/delais-reglementaires-update-drawer-object';
import { DelaisReglementairesRepository } from '../../../store/delais-reglementaires-state/delais-reglementaires-state.repository';
import { MessageService } from 'primeng/api';
import { getDelaisReglementairesAddDrawerObject } from './utils/delais-reglementaires-add-drawer-object';
import { DelaisReglementairesDto } from '@/types/dtos.types';
import { delaisReglementairesSchema } from '../../../validations/delais-reglementaires.validation';
import { TranslateService } from '@ngx-translate/core';
import { createServerQuery } from '@/components/data-table-builder/server-query';
import { RefParamsService } from '../ref-params/ref-params.service';
import { ContractingAuthorityService } from '../contracting-authority/contracting-authority.service';
import { RefTypeEnum } from '@/types';

@Component({
  selector: 'sm-delais-reglementaires',
  imports: [
    CommonModule,
    ButtonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    TagModule,
    MenuModule,
    InputTextModule,
    FormsModule,
    SelectModule,
    PageHeaderComponent,
    DrawerComponent,
    DataTableBuilderComponent,
  ],
  templateUrl: './delais-reglementaires.component.html',
  styleUrl: './delais-reglementaires.component.scss',
})
export class DelaisReglementairesComponent implements OnInit {
  //services
  delaisReglementairesService = inject(DelaisReglementairesService);
  refParamsService = inject(RefParamsService);
  contractingAuthorityService = inject(ContractingAuthorityService);
  delaisReglementairesRepository = inject(DelaisReglementairesRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);

  //breadcrumb
  breadcrumb = [
    {
      label: 'settings.delaisReglementaires.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.delaisReglementaires.breadcrumb.delaisReglementaires',
      routerLink: ['/settings', 'delais-reglementaires'],
    },
  ];

  //drawer
  @ViewChild('drawer') drawerComponent!: DrawerComponent;
  delaisReglementairesDrawer?: DrawerObject;

  //form-object
  typeMarcherOptions: { name: string; code: number }[] = [];
  modePassationOptions: { name: string; code: number }[] = [];
  formObject: DynamicForm = getDelaisReglementairesFormObject({
    store: this.delaisReglementairesRepository,
    typeMarcherOptions: this.typeMarcherOptions,
    modePassationOptions: this.modePassationOptions,
  });

  //data-table
  //server-query
  serverQuery = createServerQuery({});
  queryParams = computed(() => ({
    page: this.serverQuery.page(),
    pageSize: this.serverQuery.pageSize(),
    sortBy: this.serverQuery.sortBy(),
    sortOrder: this.serverQuery.sortOrder(),
    search: this.serverQuery.search(),
  }));

  //row-actions
  rowActions: DataTableRowActions = {
    editAction: () => {
      this.formObject = getDelaisReglementairesFormObject({
        store: this.delaisReglementairesRepository,
        typeMarcherOptions: this.typeMarcherOptions,
        modePassationOptions: this.modePassationOptions,
        isUpdateMode: true,
      });
      this.delaisReglementairesDrawer = getDelaisReglementairesUpdateDrawerObject({
        store: this.delaisReglementairesRepository,
        formObject: this.formObject,
        action: () => {
          const typeMarcherId = this.delaisReglementairesRepository.getNested<number>('response', 'id.typeMarcher.id');
          const modePassationId = this.delaisReglementairesRepository.getNested<number>('response', 'id.modePassation.id');
          const dto = this.delaisReglementairesRepository.get<DelaisReglementairesDto>('dto');
          if (typeMarcherId && modePassationId && dto) {
            this.editDelaisReglementaires(typeMarcherId, modePassationId, dto);
          }
        },
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
      });
      this.drawerComponent.toggleVisible();
    },
  };

  loading = true;
  data: DelaisReglementaires[] = [];
  totalRecords = 0;
  dataTableObject: DynamicDataTable = getDelaisReglementairesDataTableStructure({
    store: this.delaisReglementairesRepository,
    createAction: () => {
      // Reset the store and create a new form object for add mode
      this.delaisReglementairesRepository.reset();
      this.formObject = getDelaisReglementairesFormObject({
        store: this.delaisReglementairesRepository,
        typeMarcherOptions: this.typeMarcherOptions,
        modePassationOptions: this.modePassationOptions,
        isUpdateMode: false,
      });
      this.delaisReglementairesDrawer = getDelaisReglementairesAddDrawerObject({
        store: this.delaisReglementairesRepository,
        formObject: this.formObject,
        action: () => this.addDelaisReglementaires(),
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
      });
      this.drawerComponent.toggleVisible();
    },
    rowActions: this.rowActions,
    serverQuery: this.serverQuery,
  });

  constructor() {
    effect(() => {
      this.serverQuery.page();
      this.serverQuery.pageSize();
      this.serverQuery.search();
      this.serverQuery.sortBy();
      this.serverQuery.sortOrder();
      this.loadData();
    });
  }

  ngOnInit(): void {
    this.loadOptions();
    this.loadData();
  }

  loadOptions() {
    // Load type marcher options
    this.refParamsService.getSpecializedRefParams(RefTypeEnum.MARKET_TYPE).subscribe({
      next: (refs: any) => {
        this.typeMarcherOptions = refs.map((ref: any) => ({ name: ref.description, code: ref.id }));
        this.updateFormObject();
      },
      error: () => {
        this.typeMarcherOptions = [];
        this.updateFormObject();
      },
    });

    // Load mode passation options
    this.contractingAuthorityService.getModePassations().subscribe({
      next: (response: any) => {
        this.modePassationOptions = response.content.map((mp: any) => ({ name: mp.libelle, code: mp.id }));
        this.updateFormObject();
      },
      error: () => {
        this.modePassationOptions = [];
        this.updateFormObject();
      },
    });
  }

  updateFormObject() {
    this.formObject = getDelaisReglementairesFormObject({
      store: this.delaisReglementairesRepository,
      typeMarcherOptions: this.typeMarcherOptions,
      modePassationOptions: this.modePassationOptions,
    });
  }

  loadData() {
    this.delaisReglementairesService
      .getDelaisReglementaires({
        page: this.serverQuery.page(),
        size: this.serverQuery.pageSize(),
        sort: this.serverQuery.sortBy() ? `${this.serverQuery.sortBy()},${this.serverQuery.sortOrder()}` : '',
        search: this.serverQuery.search(),
      })
      .subscribe({
        next: data => {
          this.data = data.content;
          this.totalRecords = data.totalElements;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.delaisReglementaires.message.error.loading'),
          });
        },
      });
  }

  addDelaisReglementaires() {
    const dto = this.delaisReglementairesRepository.get<DelaisReglementairesDto>('dto');
    if (!dto) return;

    const result = delaisReglementairesSchema.safeParse(dto);
    if (result.success) {
      this.delaisReglementairesService.addDelaisReglementaires(dto).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData();
          this.delaisReglementairesRepository.reset();
          this.loading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.delaisReglementaires.message.success.added'),
          });
        },
        error: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.delaisReglementaires.message.error.added'),
          });
        },
      });
    } else {
      this.delaisReglementairesRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  editDelaisReglementaires(typeMarcherId: number, modePassationId: number, delaisReglementaires: DelaisReglementairesDto) {
    const dto = this.delaisReglementairesRepository.get<DelaisReglementairesDto>('dto');
    if (!dto) return;

    const result = delaisReglementairesSchema.safeParse(dto);
    if (result.success) {
      this.delaisReglementairesService.updateDelaisReglementaires(dto).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData();
          this.delaisReglementairesRepository.reset();
          this.loading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.delaisReglementaires.message.success.edited'),
          });
        },
        error: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.delaisReglementaires.message.error.edited'),
          });
        },
      });
    } else {
      this.delaisReglementairesRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }
}
